<template>
    <div>
        <Loading :loading="loading"/>
        <Edit
            :tipo="tipoSelecionado"
            :campos="camposEditados"
            :close="closeEdit"
            :onSubmit="onSubmit"
            :formatos="formatos"
            :funcoes-catalogo="funcoesCatalogo"
            :operadores="operadores"
            :todosCampos="campos"
            :grupos="grupos"
        />
        <CAlert show color="light">
            Campos mapeados são utilizados no momento da integração com plataformas de terceiros,
            é uma forma de normatizar tanto os dados de saída quanto os de entrada.
        </CAlert>

        <div class="search-bar">
            <fa-icon icon="search" class="search-icon" />
            <input
                v-model="termoBuscaInput"
                class="form-control search-input"
                type="text"
                placeholder="Buscar por nome ou chave do tipo de mapeamento..."
            />
            <CButton
                v-if="termoBuscaInput"
                color="link"
                size="sm"
                class="search-clear"
                @click="termoBuscaInput = ''; termoBusca = ''"
                aria-label="Limpar busca"
            >
                <fa-icon icon="times" />
            </CButton>
            <div class="filtros-status">
                <label class="filtro-radio">
                    <input type="radio" v-model="filtroStatus" value="todos" /> Todos
                </label>
                <label class="filtro-radio">
                    <input type="radio" v-model="filtroStatus" value="configurados" /> Configurados
                </label>
                <label class="filtro-radio">
                    <input type="radio" v-model="filtroStatus" value="vazios" /> Vazios
                </label>
            </div>
        </div>

        <div v-if="gruposFiltrados.length === 0" class="sem-resultados">
            <fa-icon icon="search" />
            Nenhum tipo encontrado para "{{ termoBusca }}"
        </div>

        <div class="grupos-grid">
            <div
                v-for="({ chaveGrupo, grupo }) in gruposFiltrados"
                :key="chaveGrupo"
                class="grupo-card"
            >
                <div class="grupo-header">
                    <div class="grupo-icon">
                        <fa-icon :icon="getIconeGrupo(chaveGrupo)" />
                    </div>
                    <div class="grupo-info">
                        <div class="grupo-nome">{{ grupo.titulo }}</div>
                        <div class="grupo-desc">{{ Object.keys(grupo.tipos).length }} tipo(s)</div>
                    </div>
                </div>

                <div class="tipos-list">
                    <div
                        v-for="({ chave, tipo }) in tiposOrdenados(grupo)"
                        :key="chave"
                        class="tipo-item"
                        @click="openEdit(chave, tipo)"
                    >
                        <div class="tipo-info">
                            <div class="tipo-nome">{{ tipo.titulo }}</div>
                            <div class="tipo-chave">{{ chave }}</div>
                        </div>
                        <div class="tipo-meta">
                            <span class="badge-campos" :class="badgeClass(chave)">
                                <fa-icon :icon="getQtdCampos(chave) > 0 ? 'check' : 'minus'" />
                                {{ getQtdCampos(chave) }}
                            </span>
                            <fa-icon icon="pencil-alt" class="tipo-edit-icon" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
  import { get, put } from '../../helpers/apiRequest';
  import clone from 'just-clone';
  import Edit from "./components/Edit";

  export default {
    name: "CamposIntegracao",
    components: {
      Edit
    },
    data() {
      return {
        loading: false,
        grupos: {},
        campos: {},
        formatos: {},
        funcoesCatalogo: [],
        operadores: [],
        tipoSelecionado: {},
        camposEditados: [],
        termoBuscaInput: '',
        termoBusca: '',
        _buscaTimeout: null,
        filtroStatus: 'todos',
      };
    },
    watch: {
      // Debounce de 250ms: evita reprocessar gruposFiltrados a cada keystroke
      // — O(grupos × tipos) pode pesar com 100+ tipos em mobile.
      termoBuscaInput(val) {
        clearTimeout(this._buscaTimeout);
        this._buscaTimeout = setTimeout(() => {
          this.termoBusca = val;
        }, 250);
      },
    },
    computed: {
      gruposFiltrados() {
        const termo = this.termoBusca.trim().toLowerCase();
        const result = [];
        for (const [chaveGrupo, grupo] of Object.entries(this.grupos || {})) {
          const tiposFiltrados = {};
          for (const [chave, tipo] of Object.entries(grupo.tipos || {})) {
            const matchTermo = !termo
              || tipo.titulo.toLowerCase().includes(termo)
              || chave.toLowerCase().includes(termo);
            const qtd = this.getQtdCampos(chave);
            const matchStatus = this.filtroStatus === 'todos'
              || (this.filtroStatus === 'configurados' && qtd > 0)
              || (this.filtroStatus === 'vazios' && qtd === 0);
            if (matchTermo && matchStatus) tiposFiltrados[chave] = tipo;
          }
          if (Object.keys(tiposFiltrados).length > 0) {
            result.push({ chaveGrupo, grupo: { ...grupo, tipos: tiposFiltrados } });
          }
        }
        return result;
      },
    },
    methods: {
      getIconeGrupo(chave) {
        const icones = {
          cliente: 'user',
          pedido: 'clipboard-list',
          lead: 'user-tie',
          logistica: 'truck',
          sac: 'headset',
        };
        return icones[chave] || 'cogs';
      },
      getQtdCampos(tipo) {
        return this.campos[tipo] ? this.campos[tipo].length : 0;
      },
      badgeClass(chave) {
        const qtd = this.getQtdCampos(chave);
        if (qtd === 0) return 'badge-campos--vazio';
        if (qtd < 5) return 'badge-campos--parcial';
        return 'badge-campos--configurado';
      },
      tiposOrdenados(grupo) {
        // Tipos configurados (>0 campos) primeiro, depois vazios — ordem alfabética dentro de cada grupo
        return Object.entries(grupo.tipos || {})
          .map(([chave, tipo]) => ({ chave, tipo, qtd: this.getQtdCampos(chave) }))
          .sort((a, b) => {
            if ((a.qtd > 0) !== (b.qtd > 0)) return a.qtd > 0 ? -1 : 1;
            return a.tipo.titulo.localeCompare(b.tipo.titulo);
          });
      },
      openEdit(chaveTipo, tipo) {
        this.tipoSelecionado = clone(tipo);
        this.tipoSelecionado.chave = chaveTipo;
        this.camposEditados = this.campos[chaveTipo] ? clone(this.campos[chaveTipo]) : [];
        this.$modal.show('editCampo');
      },
      closeEdit() {
        this.$modal.hide('editCampo');
      },
      loadGrupos() {
        get('/admin/grupos-campos-integracao')
          .then((response) => this.grupos = response);
      },
      loadCampos() {
        get('/admin/campos-integracao')
          .then((response) => this.campos = response);
      },
      loadFormatos() {
        get('/admin/campos-integracao/formatos')
          .then((response) => this.formatos = response);
      },
      loadFuncoes() {
        get('/admin/campos-integracao/funcoes')
          .then((response) => this.funcoesCatalogo = Array.isArray(response) ? response : []);
      },
      loadOperadores() {
        get('/admin/campos-integracao/operadores')
          .then((response) => this.operadores = response);
      },
      onSubmit(campos, tipo) {
        this.loading = true;
        put(`/admin/campos-integracao/${tipo}`, campos)
          .then((response) => {
            const message = (response && (response.message || (response.data && response.data.message)))
              || 'Mapeamentos atualizados com sucesso.';
            this.$swal({
              title: 'Sucesso!',
              text: message,
              icon: 'success',
            });
            this.loadCampos();
            this.closeEdit();
            this.loading = false;
          })
          .catch((err) => {
            this.$swal({
              title: 'Ops!',
              text: err.message,
              icon: 'error',
            });
            this.loading = false;
          });
      }
    },
    beforeMount() {
      this.loadGrupos();
      this.loadCampos();
      this.loadFormatos();
      this.loadFuncoes();
      this.loadOperadores();
    }
  }
</script>

<style scoped>
.search-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #fff;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
}
.search-icon {
  color: var(--text-subtle);
  flex-shrink: 0;
}
.search-input {
  border: none;
  outline: none;
  box-shadow: none;
  flex: 1;
  min-width: 200px;
  padding: 4px 0;
  font-size: 14px;
}
.search-input:focus { box-shadow: none; }
.search-clear { padding: 4px 6px !important; }
.filtros-status {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: var(--text-muted);
}
.filtro-radio {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  cursor: pointer;
}
.sem-resultados {
  margin-top: 16px;
  padding: 30px;
  text-align: center;
  color: var(--text-subtle);
  border: 1px dashed var(--border-medium);
  border-radius: 8px;
}
.sem-resultados ::v-deep svg { margin-right: 6px; }

.grupos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
  margin-top: 16px;
}

.grupo-card {
  border: 1px solid var(--border-medium);
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

.grupo-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-default);
  background: #f8f9fa;
}

.grupo-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #eceaf3;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary);
  font-size: 16px;
  flex-shrink: 0;
}

.grupo-nome {
  font-weight: 600;
  font-size: 15px;
  color: var(--text-darkest);
}

.grupo-desc {
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 2px;
}

.tipos-list {
  padding: 8px 0;
}

.tipo-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  cursor: pointer;
  transition: background 0.15s;
}

.tipo-item:hover {
  background: var(--bg-subtle);
}

.tipo-nome {
  font-weight: 500;
  font-size: 14px;
  color: var(--text-strong);
}

.tipo-chave {
  font-size: 11px;
  color: var(--text-subtle);
  margin-top: 2px;
  font-family: monospace;
}

.tipo-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.badge-campos {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  min-width: 36px;
  justify-content: center;
}
.badge-campos--vazio {
  border: 1px dashed var(--border-medium);
  color: var(--text-subtle);
  background: transparent;
}
.badge-campos--parcial {
  border: 1px solid #fde68a;
  color: #b45309;
  background: #fffbeb;
}
.badge-campos--configurado {
  border: 1px solid #a7f3d0;
  color: #047857;
  background: #ecfdf5;
}

.tipo-edit-icon {
  color: var(--text-subtle);
  font-size: 13px;
  transition: color 0.15s;
}

.tipo-item:hover .tipo-edit-icon {
  color: var(--primary);
}
</style>
